import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { ArrowRight, FileText, Loader2, PenLine, RefreshCw, Shuffle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { AiGeneratedNote, resolveCompliance } from "@/entities/compliance";
import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { errorText } from "@/shared/lib/apiErrors";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import {
  flowService,
  isInsufficientCredits,
  type FlowImagePanel as ImagePanelData,
  type FlowImageSlot,
  type FlowSuggestion,
  type ImageQuote,
  type SuggestionValue,
} from "../api/flowService";
import { websiteService, type HostSlots, type TemplateSummary } from "../api/websiteService";
import { setTheme } from "../model/hostExtras";
import { absoluteHostUrl } from "../model/hostNodes";
import { previewHost } from "../model/siteLabels";
import { isLaunched, sitePath } from "../model/siteStep";
import { FlowImagePanel } from "./components/flow/FlowImagePanel";
import { GenerateProgress } from "./components/flow/GenerateProgress";
import { NewsStatusEntry } from "./components/flow/NewsStatusEntry";
import { QuoteConfirmDialog } from "./components/flow/QuoteConfirmDialog";
import { SuggestionsPanel } from "./components/flow/SuggestionsPanel";
import { LivePreview } from "./components/host/HostPreviewCard";
import { HostThemePanel } from "./components/host/HostThemePanel";
import { logoColor } from "./components/host/logoColor";
import { useDraftEditor } from "./hooks/useDraftEditor";
import { useFlowJob } from "./hooks/useFlowJob";
import { SiteShell } from "./SiteShell";

/**
 * 预览区 / 右侧面板的高度 ≈ 视口高度减去页头(返回 + 标题 + 三段条 + 站点页标签 ≈ 200px);
 * 窄屏(<lg)不固定,上下排。
 */
const PANE_HEIGHT = "h-[70dvh] lg:h-[calc(100dvh-200px)] lg:min-h-[480px]";
const SIDE_HEIGHT = "lg:h-[calc(100dvh-200px)] lg:min-h-[480px]";

const card = cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS);

/**
 * ② 预览调整(1010):主体 = 免费子域名上的真实站点 iframe(桌面 / 手机);右侧 = 补充建议 / 改文字 / 换图 / 换配色 / 换模板。
 *
 * ★ 换图走 `PUT flow/images`(只改库里草稿),换配色写本地草稿(置脏);「更新预览」= 有本地改动先存 → `POST flow/republish` → iframe 重载。
 *   换图前若有本地未存改动先存,换完重拉草稿 —— 否则本地那份旧草稿再保存会把刚换的图盖掉。
 * ★ AI 重新生成(单张扣费,先确认)是异步任务,结束 api 自动重发预览;补充建议填写复用 `PUT collect/answers`,再跑 generate(mode=refresh,不扣费)。
 * ★ 改文字复用「内容编辑」(高级编辑);换模板在正式上线前(launched=false)都能换:报价确认后 generate {templateCode, mode:"full"}
 *   (api 删旧预览站、用新模板重新生成),进度同生成;正式上线后标「即将上线」。
 */
export default function PreviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // 1011 算法备案 §5:生成文案预览处「内容由AI生成」(租户 compliance.visibleLabel,缺省开)
  const { permissions } = useAuth();
  const showAiLabel = resolveCompliance(permissions?.compliance).visibleLabel;
  const confirm = useConfirm();
  const ed = useDraftEditor(id);
  const { site, template, content, dirty, saving } = ed;
  const [slots, setSlots] = useState<HostSlots | null>(null);
  const [images, setImages] = useState<ImagePanelData | null>(null);
  const [suggestions, setSuggestions] = useState<FlowSuggestion[] | null>(null);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [stale, setStale] = useState(false);
  const [working, setWorking] = useState(false);
  const [regenToken, setRegenToken] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [pickCode, setPickCode] = useState("");
  const [switchQuote, setSwitchQuote] = useState<ImageQuote | null>(null);
  const [switchGallery, setSwitchGallery] = useState<string[]>([]);
  const [quoting, setQuoting] = useState(false);

  const loadSuggestions = useCallback(() => {
    setSuggestionsError(null);
    flowService
      .suggestions(id)
      .then(setSuggestions)
      .catch((e) => setSuggestionsError(errorText(e)));
  }, [id]);

  const loadImages = useCallback(() => {
    flowService
      .images(id)
      .then(setImages)
      .catch(() => setImages(null));
  }, [id]);

  const task = useFlowJob(id, {
    resume: true,
    onSettled: (t) => {
      setRegenToken(null);
      loadImages();
      if (t.status === "failed") return;
      setStale(false);
      setReload((n) => n + 1);
      loadSuggestions();
      void ed.load();
    },
  });

  useEffect(() => {
    let cancelled = false;
    websiteService
      .hostSlots(id)
      .then((r) => !cancelled && setSlots(r))
      .catch(() => !cancelled && setSlots(null));
    loadSuggestions();
    loadImages();
    websiteService
      .listTemplates()
      .then((list) => {
        if (cancelled) return;
        const host = list.filter((t) => t.hostTemplate);
        setTemplates(host.length > 0 ? host : list);
      })
      .catch(() => !cancelled && setTemplates([]));
    return () => {
      cancelled = true;
    };
  }, [id, loadSuggestions, loadImages]);

  const guardLink = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty) return;
    e.preventDefault();
    void ed.confirmLeave().then((ok) => {
      if (ok) navigate(to);
    });
  };

  if (ed.error) {
    return <QueryErrorBanner isError message={ed.error} onRetry={() => void ed.load()} />;
  }
  if (ed.noDraft && site) {
    return (
      <SiteShell site={site} pageTitle="预览调整" subtitle="还没有生成官网">
        <div
          role="status"
          className={cn(
            "flex flex-col items-center gap-3 border border-dashed border-border bg-card px-6 py-12 text-center",
            CARD_WALL_RADIUS
          )}
        >
          <FileText className="h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-semibold text-foreground">还没有生成官网</h2>
          <p className="text-xs text-muted-foreground">先一键生成,生成后在这里预览和调整。</p>
          <Button asChild size="sm">
            <Link to={sitePath(id, "generate")}>去生成</Link>
          </Button>
        </div>
      </SiteShell>
    );
  }
  if (!site || !template || !content) return <Skeleton className="h-64 w-full" />;

  const running = task.phase.kind === "running";
  const busy = saving || running || working;
  const logo = typeof content.brand.logo === "string" ? content.brand.logo.trim() : "";
  const editPath = sitePath(id, "edit");
  const launchPath = sitePath(id, "launch");
  const templateLocked = isLaunched(site);
  const switchTemplates = templates.filter((t) => t.code !== site.templateCode);

  const askSwitch = async () => {
    if (!pickCode) return;
    setQuoting(true);
    try {
      const [src, q] = await Promise.all([
        flowService.sources(id).catch(() => null),
        flowService.quote(id, {
          aiImages: true,
          galleryCount: 0,
          templateCode: pickCode,
        }),
      ]);
      const gallery = src?.galleryImages ?? [];
      setSwitchGallery(gallery);
      setSwitchQuote(
        gallery.length > 0
          ? await flowService.quote(id, {
              aiImages: true,
              galleryCount: gallery.length,
              templateCode: pickCode,
            })
          : q
      );
    } catch (e) {
      toast({ title: "报价失败", description: errorText(e), variant: "destructive" });
    } finally {
      setQuoting(false);
    }
  };

  const confirmSwitch = async () => {
    const code = pickCode;
    setSwitchQuote(null);
    if (!code || !(await flush())) return;
    await task.run(async () => {
      try {
        return await flowService.generate(id, {
          templateCode: code,
          ...(switchGallery.length > 0 ? { galleryImages: switchGallery } : {}),
          aiImages: true,
          mode: "full",
        });
      } catch (e) {
        if (isInsufficientCredits(e)) throw new Error("积分不足,充值后再换模板");
        throw e;
      }
    });
  };

  /** 先把本地改动存掉(失败就停)。 */
  const flush = async () => !dirty || (await ed.persist());

  const republish = async () => {
    setWorking(true);
    try {
      if (!(await flush())) return;
      await flowService.republish(id);
      setStale(false);
      setReload((n) => n + 1);
      toast({ title: "预览已更新" });
    } catch (e) {
      toast({ title: "预览没更新成功", description: errorText(e), variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const setImage = async (token: string, body: { url?: string; useTemplate?: boolean }) => {
    setWorking(true);
    try {
      if (!(await flush())) return;
      await flowService.setImage(id, { token, ...body });
      setStale(true);
      loadImages();
      await ed.load();
    } catch (e) {
      toast({ title: "这张图没换成", description: errorText(e), variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const regenerate = async (slot: FlowImageSlot, name: string) => {
    const unit = images?.unitCredits;
    const ok = await confirm({
      title: `AI 重新生成「${name}」?`,
      description: `按这块的文字和品牌色重新画一张${
        unit != null ? `,扣 ${unit} 积分` : "(单张扣费)"
      };没生成成功不扣。完成后自动更新预览。`,
      confirmText: "重新生成",
    });
    if (!ok) return;
    if (!(await flush())) return;
    setRegenToken(slot.token);
    await task.run(async () => {
      try {
        return await flowService.regenerateImage(id, { token: slot.token });
      } catch (e) {
        setRegenToken(null);
        if (isInsufficientCredits(e)) throw new Error("积分不足,充值后再重新生成");
        throw e;
      }
    });
  };

  const submitSuggestions = async (values: SuggestionValue[]) => {
    if (!(await flush())) return;
    await task.run(async () => {
      await websiteService.answer(
        id,
        values.map((v) => ({
          questionId: `flow:${v.path}`,
          fieldPath: v.path,
          value: v.value,
          confirm: true,
        }))
      );
      return flowService.generate(id, { mode: "refresh" });
    });
  };

  const uploadSlot = async (slotToken: string, data: string) => {
    const { url } = await websiteService.uploadImage(id, {
      data,
      purpose: "slot",
      token: slotToken,
    });
    return absoluteHostUrl(url);
  };

  const runningKind = task.phase.kind === "running" ? task.phase.task?.kind : undefined;
  const progressTitle =
    runningKind === "regen_image"
      ? "正在重新生成图片"
      : runningKind === "generate"
        ? "正在用新模板重新生成"
        : "正在更新官网";

  return (
    <SiteShell
      site={site}
      pageTitle="预览调整"
      beforeLeave={ed.confirmLeave}
      subtitle={`预览地址 ${previewHost(site.previewUrl)} · ${
        dirty || stale ? "有改动还没更新到预览" : "预览已是最新"
      }`}
      actions={
        <>
          <Button
            size="sm"
            variant={dirty || stale ? "default" : "outline"}
            disabled={busy}
            onClick={() => void republish()}
          >
            {working ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            更新预览
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to={launchPath} onClick={guardLink(launchPath)}>
              下一步:上线
              <ArrowRight />
            </Link>
          </Button>
        </>
      }
    >
      <GenerateProgress
        phase={task.phase}
        title={progressTitle}
        onRetry={() => task.send({ type: "RETRY" })}
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-start">
        <section
          aria-label="站点预览"
          data-preview-pane=""
          className={cn(card, "min-w-0", PANE_HEIGHT, "lg:sticky lg:top-4")}
        >
          <LivePreview
            fill
            url={site.previewUrl}
            versionNo={site.currentVersionNo}
            reloadSignal={reload}
            heading="预览"
            note="免费子域名上的预览,改动点「更新预览」后出现在这里。"
            footnote={showAiLabel ? <AiGeneratedNote className="mt-0" /> : null}
          />
        </section>

        <div
          data-side-pane=""
          className={cn(
            "min-w-0 space-y-4 lg:overflow-y-auto lg:overscroll-contain lg:pr-1",
            SIDE_HEIGHT
          )}
        >
          <NewsStatusEntry siteId={id} status={site.news} />
          <SuggestionsPanel
            items={suggestions}
            error={suggestionsError}
            busy={busy}
            onSubmit={(v) => void submitSuggestions(v)}
          />

          <section aria-label="改文字" className={card}>
            <h2 className="text-sm font-semibold text-foreground">改文字</h2>
            <p className="text-2xs text-muted-foreground">
              选页面 / 区块,手改或让 AI 改写;改完回这里点「更新预览」。
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to={editPath} onClick={guardLink(editPath)}>
                <PenLine />
                去改文字
              </Link>
            </Button>
          </section>

          <FlowImagePanel
            panel={images}
            busy={busy}
            regenerating={regenToken}
            upload={uploadSlot}
            onSet={(token, url) => void setImage(token, { url })}
            onUseTemplate={(token) => void setImage(token, { useTemplate: true })}
            onRegenerate={(slot, name) => void regenerate(slot, name)}
          />

          {slots && (
            <HostThemePanel
              palette={slots.palette}
              theme={content.theme}
              hasLogo={logo !== ""}
              disabled={busy}
              onChange={(theme) => ed.edit((prev) => setTheme(prev, theme))}
              pickLogoColor={() => logoColor(absoluteHostUrl(logo))}
            />
          )}

          <section aria-label="换模板" className={card}>
            <h2 className="text-sm font-semibold text-foreground">换模板</h2>
            {templateLocked ? (
              <>
                <p className="text-2xs text-muted-foreground">已正式上线的站换模板即将上线。</p>
                <Button size="sm" variant="outline" disabled>
                  <Shuffle />
                  即将上线
                </Button>
              </>
            ) : (
              <>
                <p className="text-2xs text-muted-foreground">
                  当前:{site.templateName}。用同一份资料在另一套模板上重新生成(替换当前预览和草稿)。
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    aria-label="换成哪套模板"
                    className="h-8 min-w-[160px] rounded-md border border-input bg-background px-2 text-xs"
                    value={pickCode}
                    disabled={busy || switchTemplates.length === 0}
                    onChange={(e) => setPickCode(e.target.value)}
                  >
                    <option value="">选一套模板</option>
                    {switchTemplates.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || quoting || !pickCode}
                    onClick={() => void askSwitch()}
                  >
                    {quoting ? <Loader2 className="animate-spin" /> : <Shuffle />}
                    换这套重新生成
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <QuoteConfirmDialog
        quote={switchQuote}
        title="换模板重新生成?"
        description="将用同一份资料在新模板上重新生成"
        confirmText="确认换模板"
        onCancel={() => setSwitchQuote(null)}
        onConfirm={() => void confirmSwitch()}
      />
    </SiteShell>
  );
}
